/* eslint-disable @typescript-eslint/ban-ts-comment */
import { cloneDeep } from "lodash";
import { FC, useMemo } from "react";

import jsonParse from "web/utils/data/parser/string/jsonParse";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { IParsedCodeItem, IParsedOrder } from "web/types/Order";
import { IPaymentMethodItem } from "web/types/Payment";
import { MBProductType } from "web/types/Product";

import Products from "./products";
import mergePaymentMethods from "./utils/mergePaymentMethods";

interface ISuccessProductsContainerProps {
  items: IParsedOrder<MBProductType>["items"];
}

export interface IProducts
  extends Omit<
    IParsedCodeItem,
    "points_bank" | "qty_ordered" | "payment_method"
  > {
  qty: number;
  points_bank: PointsBank[];
  payment_method: IPaymentMethodItem[];
}

type PointsBank = {
  id: string;
  balance: number;
};

const SuccessProductsContainer: FC<ISuccessProductsContainerProps> = ({
  items,
}) => {
  const itemsGroupedById = useMemo((): IProducts[] => {
    if (!isArrayHasItems(items)) {
      return [];
    }

    return items.reduce<IProducts[]>((result, current) => {
      const {
        base_row_total: baseRowTotal = 0,
        base_row_total_incl_tax: baseRowTotalInclTax = 0,
        discount_amount: discountAmount,
        row_total: rowTotal,
        row_total_incl_tax: rowTotalInclTax,
        points_bank: pointsBank,
        qty_ordered: qty = 0,
        payment_method: paymentMethod,
        ...restValues
      } = current as IParsedCodeItem;

      const pointsBankParsed = isArrayHasItems(jsonParse(pointsBank))
        ? (jsonParse(pointsBank) as PointsBank[])
        : [];
      const itemIndexById = result.findIndex(
        (item) => current.product_id === item.product_id
      );

      if (itemIndexById !== -1) {
        const resultItem = result[itemIndexById];

        return result.map((item, index) =>
          index === itemIndexById
            ? {
                ...resultItem,
                base_row_total: baseRowTotal + (resultItem.base_row_total ?? 0),
                base_row_total_incl_tax:
                  baseRowTotalInclTax +
                  (resultItem.base_row_total_incl_tax ?? 0),
                discount_amount: discountAmount + resultItem.discount_amount,
                row_total: rowTotal + resultItem.row_total,
                row_total_incl_tax:
                  rowTotalInclTax + resultItem.row_total_incl_tax,
                qty: qty + resultItem.qty,
                points_bank: resultItem.points_bank.map((bank) => {
                  const bankById = pointsBankParsed.find(
                    ({ id }) => id === bank.id
                  );

                  return bankById
                    ? {
                        id: bank.id,
                        balance: bank.balance + bankById.balance,
                      }
                    : bank;
                }),
                payment_method: mergePaymentMethods(
                  resultItem.payment_method,
                  paymentMethod as IPaymentMethodItem[]
                ),
              }
            : item
        );
      }

      return [
        ...result,
        {
          ...restValues,
          base_row_total: baseRowTotal,
          base_row_total_incl_tax: baseRowTotalInclTax,
          discount_amount: discountAmount,
          row_total: rowTotal,
          row_total_incl_tax: rowTotalInclTax,
          points_bank: pointsBankParsed,
          payment_method: isArrayHasItems(paymentMethod)
            ? cloneDeep(paymentMethod as IPaymentMethodItem[])
            : [],
          qty,
        },
      ];
    }, []);
  }, [JSON.stringify(items)]);

  return isArrayHasItems(itemsGroupedById) ? (
    <Products items={itemsGroupedById} />
  ) : null;
};

export default SuccessProductsContainer;
